import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeft, Play, Pause, Trash2, Calendar, DollarSign, RefreshCw, CheckSquare, Edit } from 'lucide-react';

const GastosRecurrentes = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [actualizando, setActualizando] = useState(false);
  const [resultadoActualizacion, setResultadoActualizacion] = useState(null);
  
  // Bulk selection state
  const [selectedPlantillas, setSelectedPlantillas] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [editForm, setEditForm] = useState({
    concepto: '',
    monto: '',
    descripcion: '',
    dia_generacion: '',
    frecuencia: ''
  });
  const [saving, setSaving] = useState(false);
  const [categorias, setCategorias] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPlantillas();
  }, []);

  const fetchPlantillas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPlantillas(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategorias(data);
      }
    } catch (error) {
      console.error('Error fetching categorias:', error);
    }
  };

  const handleEdit = (plantilla) => {
    setEditingPlantilla(plantilla);
    setEditForm({
      concepto: plantilla.concepto || '',
      monto: plantilla.monto?.toString() || '',
      descripcion: plantilla.descripcion || '',
      dia_generacion: plantilla.dia_generacion?.toString() || '',
      frecuencia: plantilla.frecuencia || 'mensual',
      categoria_id: plantilla.categoria_id || ''
    });
    fetchCategorias();
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPlantilla) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas/${editingPlantilla.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          concepto: editForm.concepto,
          monto: parseFloat(editForm.monto),
          descripcion: editForm.descripcion || null,
          dia_generacion: parseInt(editForm.dia_generacion),
          frecuencia: editForm.frecuencia,
          categoria_id: editForm.categoria_id
        })
      });

      if (response.ok) {
        alert('Plantilla actualizada exitosamente');
        setShowEditModal(false);
        setEditingPlantilla(null);
        fetchPlantillas();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al actualizar la plantilla');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar la plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleActualizarGastos = async () => {
    setActualizando(true);
    setResultadoActualizacion(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/actualizar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setResultadoActualizacion(result);
        fetchPlantillas(); // Refresh the list
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al actualizar gastos');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar gastos');
    } finally {
      setActualizando(false);
    }
  };

  const handlePausar = async (plantillaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas/${plantillaId}/pausar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Plantilla pausada exitosamente');
        fetchPlantillas();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al pausar la plantilla');
    }
  };

  const handleReactivar = async (plantillaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas/${plantillaId}/reactivar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Plantilla reactivada exitosamente');
        fetchPlantillas();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al reactivar la plantilla');
    }
  };

  const handleCancelar = async (plantillaId) => {
    if (!window.confirm('¿Estás seguro? Esta acción cancelará la plantilla y no se generarán más gastos automáticamente.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas/${plantillaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Plantilla cancelada exitosamente');
        fetchPlantillas();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cancelar la plantilla');
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select only non-cancelled plantillas
      const selectableIds = plantillasFiltradas
        .filter(p => p.estatus !== 'cancelado')
        .map(p => p.id);
      setSelectedPlantillas(selectableIds);
    } else {
      setSelectedPlantillas([]);
    }
  };

  const handleSelectPlantilla = (plantillaId) => {
    setSelectedPlantillas(prev => {
      if (prev.includes(plantillaId)) {
        return prev.filter(id => id !== plantillaId);
      } else {
        return [...prev, plantillaId];
      }
    });
  };

  const handleBulkAction = async () => {
    if (selectedPlantillas.length === 0 || !bulkAction) return;
    
    setBulkProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/recurrentes/plantillas/accion-masiva`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plantilla_ids: selectedPlantillas,
          accion: bulkAction
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`${result.procesados} plantilla(s) ${bulkAction === 'pausar' ? 'pausada(s)' : 'cancelada(s)'} exitosamente`);
        setSelectedPlantillas([]);
        setShowBulkModal(false);
        setBulkAction('');
        fetchPlantillas();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al procesar la acción');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al procesar la acción');
    } finally {
      setBulkProcessing(false);
    }
  };

  const openBulkModal = (action) => {
    setBulkAction(action);
    setShowBulkModal(true);
  };

  const plantillasFiltradas = filtroEstatus
    ? plantillas.filter(p => p.estatus === filtroEstatus)
    : plantillas;

  // Check if all selectable plantillas are selected
  const selectablePlantillas = plantillasFiltradas.filter(p => p.estatus !== 'cancelado');
  const allSelected = selectablePlantillas.length > 0 && selectedPlantillas.length === selectablePlantillas.length;
  const someSelected = selectedPlantillas.length > 0 && selectedPlantillas.length < selectablePlantillas.length;

  const frecuenciaLabels = {
    mensual: 'Mensual',
    bimestral: 'Bimestral',
    trimestral: 'Trimestral',
    anual: 'Anual'
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <button
              onClick={() => navigate('/egresos')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver a Egresos
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Gastos Recurrentes</h1>
            <p className="text-gray-600 mt-1">Administra las plantillas de gastos automáticos</p>
          </div>
          <button
            onClick={handleActualizarGastos}
            disabled={actualizando}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
            data-testid="btn-actualizar-gastos"
          >
            <RefreshCw className={`h-5 w-5 ${actualizando ? 'animate-spin' : ''}`} />
            {actualizando ? 'Actualizando...' : 'Actualizar Gastos'}
          </button>
        </div>

        {/* Resultado de actualización */}
        {resultadoActualizacion && (
          <div className={`p-4 rounded-lg border ${
            resultadoActualizacion.errores > 0 
              ? 'bg-yellow-50 border-yellow-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className={`font-bold ${
                  resultadoActualizacion.errores > 0 ? 'text-yellow-800' : 'text-green-800'
                }`}>
                  Actualización Completada
                </h3>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Plantillas procesadas:</span>
                    <span className="ml-1 font-bold">{resultadoActualizacion.plantillas_procesadas}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Gastos generados:</span>
                    <span className="ml-1 font-bold text-green-600">{resultadoActualizacion.gastos_generados}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Gastos eliminados:</span>
                    <span className="ml-1 font-bold text-red-600">{resultadoActualizacion.gastos_eliminados}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Errores:</span>
                    <span className="ml-1 font-bold text-orange-600">{resultadoActualizacion.errores}</span>
                  </div>
                </div>
                {resultadoActualizacion.detalle.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                      Ver detalle ({resultadoActualizacion.detalle.length} acciones)
                    </summary>
                    <ul className="mt-2 text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                      {resultadoActualizacion.detalle.map((d, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            d.accion === 'generar_gastos' ? 'bg-green-100 text-green-700' :
                            d.accion === 'cancelar_gastos' ? 'bg-red-100 text-red-700' :
                            d.accion === 'error' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {d.accion}
                          </span>
                          <span className="font-medium">{d.plantilla}:</span>
                          {d.cantidad !== undefined && <span>{d.cantidad} gasto(s)</span>}
                          {d.mensaje && <span>{d.mensaje}</span>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <button
                onClick={() => setResultadoActualizacion(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Bulk Selection Bar */}
        {selectedPlantillas.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <span className="text-indigo-800 font-medium">
                {selectedPlantillas.length} plantilla(s) seleccionada(s)
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedPlantillas([])}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar Selección
              </button>
              <button
                onClick={() => openBulkModal('pausar')}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition flex items-center gap-2"
                data-testid="btn-bulk-pausar"
              >
                <Pause className="h-4 w-4" />
                Pausar Seleccionados
              </button>
              <button
                onClick={() => openBulkModal('cancelar')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                data-testid="btn-bulk-cancelar"
              >
                <Trash2 className="h-4 w-4" />
                Cancelar Seleccionados
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plantillas Activas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {plantillas.filter(p => p.estatus === 'activo').length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plantillas Pausadas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {plantillas.filter(p => p.estatus === 'pausado').length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Pause className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Generados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {plantillas.reduce((sum, p) => sum + (p.gastos_generados || 0), 0)}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">Estatus:</label>
            <select
              value={filtroEstatus}
              onChange={(e) => setFiltroEstatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Todos</option>
              <option value="activo">Activo</option>
              <option value="pausado">Pausado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando plantillas...</p>
            </div>
          ) : plantillasFiltradas.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron plantillas recurrentes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => { if (el) el.indeterminate = someSelected; }}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                        data-testid="select-all-plantillas"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Concepto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoría</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Frecuencia</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Día</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Próxima Generación</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Generados</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Estatus</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {plantillasFiltradas.map((plantilla, index) => (
                    <tr 
                      key={plantilla.id} 
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedPlantillas.includes(plantilla.id) ? 'bg-indigo-50' : ''}`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedPlantillas.includes(plantilla.id)}
                          onChange={() => handleSelectPlantilla(plantilla.id)}
                          disabled={plantilla.estatus === 'cancelado'}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300 disabled:opacity-50"
                          data-testid={`checkbox-plantilla-${plantilla.id}`}
                        />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        <div className="font-medium">{plantilla.concepto}</div>
                        {plantilla.ligado_a === 'contrato' && plantilla.contrato_numero && (
                          <div className="text-xs text-gray-500 mt-1">Contrato: {plantilla.contrato_numero}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                        ${plantilla.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {plantilla.categoria_nombre || 'Sin categoría'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {frecuenciaLabels[plantilla.frecuencia]}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        Día {plantilla.dia_generacion}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {new Date(plantilla.proxima_generacion).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {plantilla.gastos_generados || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {plantilla.estatus === 'activo' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Activo
                          </span>
                        )}
                        {plantilla.estatus === 'pausado' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pausado
                          </span>
                        )}
                        {plantilla.estatus === 'cancelado' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Cancelado
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex gap-2">
                          {plantilla.estatus !== 'cancelado' && (
                            <button
                              onClick={() => handleEdit(plantilla)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {plantilla.estatus === 'activo' && (
                            <button
                              onClick={() => handlePausar(plantilla.id)}
                              className="text-yellow-600 hover:text-yellow-800"
                              title="Pausar"
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          )}
                          {plantilla.estatus === 'pausado' && (
                            <button
                              onClick={() => handleReactivar(plantilla.id)}
                              className="text-green-600 hover:text-green-800"
                              title="Reactivar"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}
                          {plantilla.estatus !== 'cancelado' && (
                            <button
                              onClick={() => handleCancelar(plantilla.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Cancelar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bulk Action Confirmation Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-full ${bulkAction === 'pausar' ? 'bg-yellow-100' : 'bg-red-100'}`}>
                  {bulkAction === 'pausar' ? (
                    <Pause className={`h-6 w-6 text-yellow-600`} />
                  ) : (
                    <Trash2 className={`h-6 w-6 text-red-600`} />
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Confirmar {bulkAction === 'pausar' ? 'Pausa' : 'Cancelación'}
                </h2>
              </div>

              <p className="text-gray-600 mb-4">
                ¿Estás seguro de que deseas <strong>{bulkAction}</strong> {selectedPlantillas.length} plantilla(s)?
              </p>

              {bulkAction === 'pausar' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> Las plantillas pausadas no generarán nuevos gastos automáticamente hasta que se reactiven.
                  </p>
                </div>
              )}

              {bulkAction === 'cancelar' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-800">
                    <strong>Advertencia:</strong> Las plantillas canceladas no podrán reactivarse. Los gastos ya generados se marcarán como cancelados.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowBulkModal(false); setBulkAction(''); }}
                  disabled={bulkProcessing}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkAction}
                  disabled={bulkProcessing}
                  className={`px-4 py-2 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-50 ${
                    bulkAction === 'pausar' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-red-600 hover:bg-red-700'
                  }`}
                  data-testid="confirm-bulk-action"
                >
                  {bulkProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      {bulkAction === 'pausar' ? <Pause className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                      {bulkAction === 'pausar' ? 'Pausar' : 'Cancelar'} {selectedPlantillas.length} plantilla(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingPlantilla && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Edit className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Editar Plantilla
                </h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concepto *</label>
                  <input
                    type="text"
                    value={editForm.concepto}
                    onChange={(e) => setEditForm({ ...editForm, concepto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: Cuota de mantenimiento"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.monto}
                    onChange={(e) => setEditForm({ ...editForm, monto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    value={editForm.descripcion}
                    onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="Descripción opcional..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                  <select
                    value={editForm.categoria_id}
                    onChange={(e) => setEditForm({ ...editForm, categoria_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría...</option>
                    {categorias.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia *</label>
                    <select
                      value={editForm.frecuencia}
                      onChange={(e) => setEditForm({ ...editForm, frecuencia: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="mensual">Mensual</option>
                      <option value="bimestral">Bimestral</option>
                      <option value="trimestral">Trimestral</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Día de generación *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={editForm.dia_generacion}
                      onChange={(e) => setEditForm({ ...editForm, dia_generacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1-31"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => { setShowEditModal(false); setEditingPlantilla(null); }}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || !editForm.concepto || !editForm.monto || !editForm.dia_generacion}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-edit-plantilla"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Guardando...
                    </>
                  ) : (
                    'Guardar Cambios'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GastosRecurrentes;